import type { RequestContext } from "../context/request-context.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../domain/identity/access-types.js";
import type { Equipment } from "../../equipment/equipment-domain.js";
import type { EquipmentService } from "../../equipment/equipment-service.js";
import type { IdentityRepository } from "../../infrastructure/database/memory/in-memory-identity-repositories.js";
import { ProfessionalType, ProfessionalStatus, type Professional } from "../../domain/identity/professional.js";
import { linesOfSystem } from "../../domain/inspection-book/line-list.js";
import { OFFSITE_TUBOVIA_GROUP_ID } from "../../domain/dashboard/dashboard-indicators.js";
import {
  applyProgressChange,
  newFieldInspectionProgress,
  statusOf,
  summarizeFieldProgress,
  summarizeGroupedBy,
  FieldProgressRuleError,
  type FieldControlRepository,
  type FieldInspectionAssignment,
  type FieldInspectionProgress,
  type FieldInspectionStatus,
  type FieldProgressChange,
  type FieldProgressSummary
} from "../../domain/field-control/field-inspection-progress.js";

/**
 * CONTROLE DE CAMPO — serviço de aplicação.
 *
 * Responde a três perguntas, todas pela mesma fonte:
 *   MINHAS LINHAS   quais Linhas o Inspetor logado pode apontar hoje;
 *   ACOMPANHAMENTO  como está o avanço de um Inspetor / de um recorte;
 *   GERENCIAMENTO   quem está vinculado a qual Sistema.
 *
 * O escopo do Inspetor NUNCA vem da interface: é derivado aqui, do
 * `professionalId` da sessão contra os vínculos gravados. Um Inspetor não
 * enxerga (nem aponta) Linha de Sistema ao qual não foi vinculado, mesmo que
 * chame o serviço diretamente.
 */

/** Linha com o avanço já resolvido — o que a tela e o Painel consomem. */
export interface FieldControlLineRow {
  lineEquipmentId: string;
  lineTag: string;
  systemId: string | null;
  systemTag: string;
  fluid: string | null;
  from: string | null;
  to: string | null;
  progress: FieldInspectionProgress;
  status: FieldInspectionStatus;
}

export interface FieldControlScope {
  rows: FieldControlLineRow[];
  summary: FieldProgressSummary;
  /** Sistemas no escopo, para os filtros da tela. */
  systems: Array<{ id: string; tag: string }>;
}

export interface FieldControlFilter {
  clientId?: string;
  projectId?: string;
  unitAreaId?: string;
  /** Acompanhamento de um Inspetor específico — exige `view_all`. */
  inspectorProfessionalId?: string;
  systemId?: string;
  lineEquipmentId?: string;
}

export interface UpdateProgressInput {
  lineEquipmentId: string;
  /** Versão que o usuário tinha em tela. Divergiu = alguém gravou antes (item 41). */
  expectedVersion: number;
  change: FieldProgressChange;
}

export interface CreateAssignmentInput {
  inspectorProfessionalId: string;
  clientId: string;
  serviceOrderId?: string | null;
  projectId?: string | null;
  unitAreaId?: string | null;
  targetType?: "SYSTEM" | "LINE";
  targetId?: string;
  /** Legacy input: when present, targetType defaults to SYSTEM. */
  systemId?: string | null;
}

const VERSION_CONFLICT = "FIELD_PROGRESS_VERSION_CONFLICT";
const CONFLICT_MESSAGE = "Esta Linha foi atualizada por outro usuário. Atualize os dados antes de salvar novamente.";

/** Tipos profissionais elegíveis para a função de Inspetor de Campo. */
const ELIGIBLE_INSPECTOR_TYPES = new Set<string>([
  ProfessionalType.Inspector,
  ProfessionalType.NdtInspector
]);

export class FieldControlService {
  private readonly auth = new RbacAuthorizationService();

  constructor(
    private readonly repo: FieldControlRepository,
    private readonly equipmentService: EquipmentService,
    private readonly audit?: AuditRecorder,
    /** Opcional: quando presente, createAssignment valida a elegibilidade do inspetor. */
    private readonly identityRepo?: IdentityRepository
  ) {}

  // ------------------------------------------------------------- consultas

  /**
   * LISTA OFICIAL DE INSPETORES ELEGÍVEIS (Controle de Campo).
   *
   * Fonte única da regra: ELIGIBLE_INSPECTOR_TYPES (mesma usada por
   * createAssignment). Inspetores são PROFESSIONALS GLOBAIS — NÃO filtra
   * por clientId. O contexto do Cliente pertence à ATRIBUIÇÃO, não ao
   * cadastro do profissional.
   *
   * Retorna somente profissionais ATIVOS com tipo elegível. Vínculos
   * legados com tipos não elegíveis permanecem no histórico, mas novos
   * vínculos são bloqueados por createAssignment.
   */
  /**
   * CORRETIVO P0 — Painel/Dashboard: filtro de Inspetores não deve exigir
   * field_control.view_all. A permissão correta é professional.read, que já
   * está presente em todos os perfis que acessam o Painel (Inspetor, Técnico,
   * Engenharia, Admin). field_control.view_all pertence ao módulo Controle de
   * Campo e não deve ser requisito para leitura de profissionais em filtros.
   */
  public async listEligibleInspectors(context: RequestContext): Promise<Professional[]> {
    this.auth.require(context, PermissionCode.ProfessionalRead);
    if (!this.identityRepo) return [];
    // Profissionais globais: NÃO passar activeClientId para evitar que o
    // repositório aplique WHERE client_id = ? e descarte profissionais
    // globais (client_id = NULL).
    const all = await this.identityRepo.listProfessionals();
    return all.filter(p =>
      !p.deletedAt &&
      p.status === ProfessionalStatus.Active &&
      ELIGIBLE_INSPECTOR_TYPES.has(String(p.professionalType ?? "").trim())
    );
  }

  /**
   * MINHAS LINHAS — só os Sistemas vinculados ao Profissional da sessão.
   * Sem `professionalId` na sessão não há escopo: devolve vazio em vez de
   * "todas", porque o default nunca pode ser mais permissivo.
   */
  public async listMyLines(context: RequestContext): Promise<FieldControlScope> {
    this.auth.require(context, PermissionCode.FieldControlViewOwn);
    const professionalId = context.professionalId ? String(context.professionalId) : null;
    if (!professionalId) return { rows: [], summary: summarizeFieldProgress([]), systems: [] };
    const assignments = await this.repo.listAssignments({
      clientId: context.activeClientId ? String(context.activeClientId) : undefined,
      inspectorProfessionalId: professionalId,
      activeOnly: true
    });
    const systemIds = assignments.map(a => a.systemId).filter((id): id is string => !!id);
    const directLineIds = assignments.filter(a => a.targetType === "LINE").map(a => a.targetId);
    return this.buildScope(context, systemIds, {}, directLineIds);
  }

  /**
   * ACOMPANHAMENTO — recorte livre (Cliente/Projeto/Unidade/Inspetor/Sistema)
   * para quem acompanha a operação inteira.
   */
  public async listScope(context: RequestContext, filter: FieldControlFilter = {}): Promise<FieldControlScope> {
    this.auth.require(context, PermissionCode.FieldControlViewAll);
    let systemIds: string[] | null = null;
    let directLineIds: string[] = [];
    if (filter.inspectorProfessionalId) {
      const assignments = await this.repo.listAssignments({
        clientId: filter.clientId, inspectorProfessionalId: filter.inspectorProfessionalId, activeOnly: true
      });
      systemIds = assignments.map(a => a.systemId).filter((id): id is string => !!id);
      directLineIds = assignments.filter(a => a.targetType === "LINE").map(a => a.targetId);
    }
    if (filter.systemId) {
      systemIds = systemIds ? systemIds.filter(id => id === filter.systemId) : [filter.systemId];
    }
    if (filter.lineEquipmentId) {
      directLineIds = directLineIds.filter(id => id === filter.lineEquipmentId);
    }
    return this.buildScope(context, systemIds, filter, directLineIds);
  }

  /**
   * Monta o escopo: Sistemas → Linhas do cadastro → avanço gravado. A Linha
   * sem apontamento aparece como 0% / NÃO INICIADA sem nada ser gravado —
   * o registro só nasce quando alguém aponta de fato.
   */
  private async buildScope(
    context: RequestContext,
    systemIds: readonly string[] | null,
    filter: FieldControlFilter = {},
    directLineIds: readonly string[] = []
  ): Promise<FieldControlScope> {
    const clientId = filter.clientId ?? (context.activeClientId ? String(context.activeClientId) : undefined);
    const equipments = await this.equipmentService.listEquipments(context, { status: "ALL", clientId } as never) as Equipment[];

    let systems = equipments.filter(eq => eq.equipmentType === "PIPING_SYSTEM" && eq.status === "ACTIVE" && !eq.deletedAt);
    if (systemIds !== null) {
      const allowed = new Set(systemIds);
      systems = systems.filter(system => allowed.has(String(system.id)));
    }
    if (filter.projectId) systems = systems.filter(system => String(system.projectId ?? "") === filter.projectId);
    if (filter.unitAreaId) systems = systems.filter(system => String(system.unitAreaId ?? system.areaId ?? "") === filter.unitAreaId);

    const linesById = new Map<string, { line: Equipment; system: Equipment | null }>();
    for (const system of systems) {
      for (const line of linesOfSystem(system, equipments)) {
        if (!filter.lineEquipmentId || String(line.id) === filter.lineEquipmentId) {
          linesById.set(String(line.id), { line, system });
        }
      }
    }

    // P3: Linhas NOT_APPLICABLE / sem Sistema entram diretamente no escopo.
    // REGRA V4-CAUSA1: quando NÃO há filtro de inspetor (escopo geral do Dashboard),
    // TODAS as linhas sem Sistema ativo devem ser incluídas automaticamente,
    // não apenas as listadas em directLineIds (que só existem com inspectorProfessionalId).
    // Com filtro de inspetor, mantém o comportamento atual: apenas linhas atribuídas.
    const includeAllOffsiteLines = !filter.inspectorProfessionalId;
    const directSet = directLineIds.length > 0 ? new Set(directLineIds.map(String)) : null;

    for (const line of equipments) {
      if (line.equipmentType !== "PIPING_LINE" || line.status !== "ACTIVE" || line.deletedAt) continue;
      // Já foi adicionada via sistema? Pula para evitar duplicação.
      if (linesById.has(String(line.id))) continue;
      // Linha tem sistema ativo? Então já deveria ter entrado via linesOfSystem.
      // Só processamos aqui linhas SEM sistema (OFF-SITE/TUBOVIA).
      const pipingSysId = String(line.pipingSystemId || "");
      if (pipingSysId) continue;

      // Filtros estruturais
      if (filter.lineEquipmentId && String(line.id) !== filter.lineEquipmentId) continue;
      if (filter.projectId && String(line.projectId ?? "") !== filter.projectId) continue;
      if (filter.unitAreaId && String(line.unitAreaId ?? line.areaId ?? "") !== filter.unitAreaId) continue;

      // Escopo geral: inclui todas as linhas OFF-SITE.
      // Escopo por inspetor: inclui apenas se estiver em directLineIds.
      if (includeAllOffsiteLines || (directSet && directSet.has(String(line.id)))) {
        linesById.set(String(line.id), { line, system: null });
      }
    }

    const allLineIds = [...linesById.keys()];
    const storedByLine = new Map<string, FieldInspectionProgress>();
    for (const stored of await this.repo.listProgress({
      clientId,
      systemIds: systems.length > 0 ? systems.map(s => String(s.id)) : undefined,
      lineEquipmentIds: allLineIds
    })) {
      storedByLine.set(stored.lineEquipmentId, stored);
    }

    const now = new Date().toISOString();
    const rows: FieldControlLineRow[] = [];
    for (const { line, system } of linesById.values()) {
      const lineId = String(line.id);
      const systemId = system ? String(system.id) : null;
      const progress = storedByLine.get(lineId) ?? newFieldInspectionProgress({
        id: `FP-${lineId}`,
        clientId: String(line.clientId),
        serviceOrderId: line.serviceOrderId ? String(line.serviceOrderId) : null,
        projectId: line.projectId ?? null,
        unitAreaId: line.unitAreaId ?? (line.areaId ? String(line.areaId) : null),
        systemId,
        targetType: system ? "SYSTEM" : "LINE",
        targetId: systemId ?? lineId,
        lineEquipmentId: lineId,
        now
      });
      rows.push({
        lineEquipmentId: lineId,
        lineTag: line.tag || line.lineNumber || lineId,
        systemId,
        systemTag: system ? (system.tag || String(system.id)) : "NÃO APLICÁVEL",
        fluid: line.fluid ?? line.service ?? null,
        from: line.fromReference ?? null,
        to: line.toReference ?? null,
        progress,
        status: statusOf(progress)
      });
    }

    return {
      rows,
      summary: summarizeFieldProgress(rows.map(row => row.progress)),
      systems: systems.map(system => ({ id: String(system.id), tag: system.tag || String(system.id) }))
    };
  }

  /** Avanço consolidado por Sistema / por Unidade / por Inspetor (itens 36/37/39). */
  public async summarizeBySystem(context: RequestContext, filter: FieldControlFilter = {}): Promise<Map<string, FieldProgressSummary>> {
    const scope = await this.listScope(context, filter);
    // REGRA V4-CAUSA4: Linhas sem systemId são agrupadas sob OFFSITE_TUBOVIA_GROUP_ID
    // em vez de serem descartadas pelo filtro `key === null` em summarizeGroupedBy.
    return summarizeGroupedBy(
      scope.rows.map(row => row.progress),
      progress => progress.systemId || OFFSITE_TUBOVIA_GROUP_ID
    );
  }

  // ------------------------------------------------------------- escrita

  /**
   * Aponta o avanço de UMA Linha. Todas as regras de data/status/motivo são
   * do domínio (`applyProgressChange`); aqui ficam só autorização, escopo,
   * concorrência e auditoria.
   */
  public async updateProgress(context: RequestContext, input: UpdateProgressInput): Promise<FieldInspectionProgress> {
    this.auth.require(context, PermissionCode.FieldControlUpdateOwn);
    await this.assertLineInScope(context, input.lineEquipmentId);

    const existing = await this.repo.findProgressByLine(input.lineEquipmentId);
    const current = existing ?? (await this.seedProgressFor(context, input.lineEquipmentId));
    const expectedVersion = existing ? input.expectedVersion : 0;
    if (existing && existing.version !== input.expectedVersion) {
      throw new AppError(ErrorCode.Conflict, "Field progress version conflict", { safeMessage: CONFLICT_MESSAGE });
    }

    let next: FieldInspectionProgress;
    try {
      next = applyProgressChange(current, input.change, new Date().toISOString(), context.userId ? String(context.userId) : null);
    } catch (error) {
      if (error instanceof FieldProgressRuleError) {
        throw new AppError(ErrorCode.Validation, "Field progress rule", { safeMessage: error.message });
      }
      throw error;
    }

    try {
      await this.repo.saveProgress(next, expectedVersion);
    } catch (error) {
      if ((error as Error)?.message === VERSION_CONFLICT) {
        throw new AppError(ErrorCode.Conflict, "Field progress version conflict", { safeMessage: CONFLICT_MESSAGE });
      }
      throw error;
    }

    await this.recordProgressAudit(context, current, next, input.change);
    return next;
  }

  /**
   * Atualização em massa (item 20). Cada Linha é auditada individualmente e
   * uma falha isolada não derruba o lote inteiro — a Linha que falhou volta
   * na lista de erros para o usuário decidir.
   */
  public async updateProgressBulk(
    context: RequestContext,
    lineEquipmentIds: readonly string[],
    change: FieldProgressChange
  ): Promise<{ updated: FieldInspectionProgress[]; failures: Array<{ lineEquipmentId: string; message: string }> }> {
    this.auth.require(context, PermissionCode.FieldControlUpdateOwn);
    const updated: FieldInspectionProgress[] = [];
    const failures: Array<{ lineEquipmentId: string; message: string }> = [];
    for (const lineEquipmentId of lineEquipmentIds) {
      try {
        const existing = await this.repo.findProgressByLine(lineEquipmentId);
        updated.push(await this.updateProgress(context, {
          lineEquipmentId,
          expectedVersion: existing ? existing.version : 0,
          change
        }));
      } catch (error) {
        failures.push({ lineEquipmentId, message: (error as { safeMessage?: string })?.safeMessage || (error as Error)?.message || "Falha ao atualizar a Linha." });
      }
    }
    return { updated, failures };
  }

  // --------------------------------------------------------- atribuições

  public async listAssignments(context: RequestContext, filter: { inspectorProfessionalId?: string; systemId?: string; targetType?: "SYSTEM" | "LINE"; targetId?: string } = {}): Promise<FieldInspectionAssignment[]> {
    if (context.permissions.has(PermissionCode.FieldControlManageAssignments) || context.permissions.has(PermissionCode.FieldControlViewAll)) {
      return this.repo.listAssignments({
        clientId: context.activeClientId ? String(context.activeClientId) : undefined,
        ...filter,
        activeOnly: false
      });
    }
    this.auth.require(context, PermissionCode.FieldControlViewOwn);
    if (!context.professionalId) return [];
    return this.repo.listAssignments({
      clientId: context.activeClientId ? String(context.activeClientId) : undefined,
      ...filter,
      inspectorProfessionalId: String(context.professionalId),
      activeOnly: false
    });
  }

  /** Vincula Inspetor → Sistema (item 5). Reativa o vínculo se ele já existiu. */
  public async createAssignment(context: RequestContext, input: CreateAssignmentInput): Promise<FieldInspectionAssignment> {
    this.auth.require(context, PermissionCode.FieldControlManageAssignments);
    if (!input.inspectorProfessionalId?.trim()) {
      throw new AppError(ErrorCode.Validation, "Inspector required", { safeMessage: "Selecione o Inspetor a ser vinculado." });
    }
    if (!input.clientId?.trim()) {
      throw new AppError(ErrorCode.Validation, "Client required", { safeMessage: "Selecione o Cliente do vínculo." });
    }

    // VALIDAÇÃO DE ELEGIBILIDADE NO BACKEND: somente Inspetores ativos podem
    // ser vinculados. Quando o IdentityRepository está disponível (composição
    // real), a chamada manual com Engenheiro-PLH, Tec-Doc ou inativo é recusada.
    if (this.identityRepo) {
      const professional = await this.identityRepo.getProfessional(input.inspectorProfessionalId as never);
      if (!professional) {
        throw new AppError(ErrorCode.NotFound, "Inspector not found", { safeMessage: "Inspetor não encontrado." });
      }
      if (professional.status !== ProfessionalStatus.Active || !!professional.deletedAt) {
        throw new AppError(ErrorCode.Validation, "Inspector must be active", { safeMessage: "Somente Inspetores ativos podem ser vinculados." });
      }
      const type = String(professional.professionalType ?? "").trim();
      if (!ELIGIBLE_INSPECTOR_TYPES.has(type)) {
        throw new AppError(ErrorCode.Validation, "Professional not eligible as inspector", {
          safeMessage: "O profissional selecionado não é elegível como Inspetor de Campo."
        });
      }
    }

    const targetType = input.targetType ?? "SYSTEM";
    const targetId = String(input.targetId ?? input.systemId ?? "").trim();
    const systemId = targetType === "SYSTEM" ? targetId : null;
    if (!targetId) {
      throw new AppError(ErrorCode.Validation, "Target required", { safeMessage: targetType === "LINE" ? "Selecione a Linha a ser vinculada." : "Selecione o Sistema de Tubulação." });
    }

    const existing = (await this.repo.listAssignments({
      inspectorProfessionalId: input.inspectorProfessionalId, targetType, targetId
    }))[0];

    const assignment: FieldInspectionAssignment = {
      id: existing?.id ?? `FA-${input.inspectorProfessionalId}-${targetType}-${targetId}`,
      inspectorProfessionalId: input.inspectorProfessionalId,
      clientId: input.clientId,
      serviceOrderId: input.serviceOrderId ?? null,
      projectId: input.projectId ?? null,
      unitAreaId: input.unitAreaId ?? null,
      systemId,
      targetType,
      targetId,
      active: true,
      createdBy: context.userId ? String(context.userId) : null,
      createdAt: existing?.createdAt ?? new Date().toISOString()
    };
    await this.repo.saveAssignment(assignment);
    await this.recordAudit(context, AuditAction.FieldInspectorAssigned, assignment.targetId, {
      inspectorProfessionalId: assignment.inspectorProfessionalId, targetType: assignment.targetType, targetId: assignment.targetId, systemId: assignment.systemId
    });
    return assignment;
  }

  /** Desvincula sem apagar histórico: o vínculo fica inativo. */
  public async deactivateAssignment(context: RequestContext, assignmentId: string): Promise<void> {
    this.auth.require(context, PermissionCode.FieldControlManageAssignments);
    const assignment = await this.repo.findAssignmentById(assignmentId);
    if (!assignment) {
      throw new AppError(ErrorCode.NotFound, "Assignment not found", { safeMessage: "Vínculo não encontrado." });
    }
    await this.repo.saveAssignment({ ...assignment, active: false });
    await this.recordAudit(context, AuditAction.FieldInspectorUnassigned, assignment.targetId, {
      inspectorProfessionalId: assignment.inspectorProfessionalId, targetType: assignment.targetType, targetId: assignment.targetId, systemId: assignment.systemId
    });
  }

  // ------------------------------------------------------------- internas

  /**
   * Escopo de escrita: quem tem `view_all` aponta em qualquer Linha do
   * Cliente; o Inspetor só nas Linhas dos Sistemas a que foi vinculado.
   */
  private async assertLineInScope(context: RequestContext, lineEquipmentId: string): Promise<void> {
    if (this.hasPermission(context, PermissionCode.FieldControlViewAll)) return;
    const professionalId = context.professionalId ? String(context.professionalId) : null;
    if (!professionalId) {
      throw new AppError(ErrorCode.Authorization, "No professional in session", {
        safeMessage: "Sua sessão não está vinculada a um Profissional. Solicite o vínculo à Administração."
      });
    }
    const line = await this.findLine(context, lineEquipmentId);
    const systemId = String(line.pipingSystemId ?? "");
    const assignments = await this.repo.listAssignments({
      inspectorProfessionalId: professionalId,
      ...(systemId ? { systemId } : { targetType: "LINE", targetId: String(line.id) }),
      activeOnly: true
    });
    if (assignments.length === 0) {
      throw new AppError(ErrorCode.Authorization, "Line out of inspector scope", {
        safeMessage: systemId
          ? "Esta Linha pertence a um Sistema ao qual você não está vinculado."
          : "Esta Linha não está atribuída ao seu Inspetor."
      });
    }
  }

  private hasPermission(context: RequestContext, permission: string): boolean {
    try {
      this.auth.require(context, permission as never);
      return true;
    } catch {
      return false;
    }
  }

  private async findLine(context: RequestContext, lineEquipmentId: string): Promise<Equipment> {
    const line = await this.equipmentService.getEquipment(context, lineEquipmentId) as Equipment | null;
    if (!line) {
      throw new AppError(ErrorCode.NotFound, "Line not found", { safeMessage: "Linha não encontrada no cadastro." });
    }
    return line;
  }

  private async seedProgressFor(context: RequestContext, lineEquipmentId: string): Promise<FieldInspectionProgress> {
    const line = await this.findLine(context, lineEquipmentId);
    return newFieldInspectionProgress({
      id: `FP-${lineEquipmentId}`,
      clientId: String(line.clientId),
      serviceOrderId: line.serviceOrderId ? String(line.serviceOrderId) : null,
      projectId: line.projectId ?? null,
      unitAreaId: line.unitAreaId ?? (line.areaId ? String(line.areaId) : null),
      systemId: String(line.pipingSystemId ?? ""),
      lineEquipmentId,
      now: new Date().toISOString()
    });
  }

  /** Histórico completo da alteração (item 22) — antes e depois, com o motivo. */
  private async recordProgressAudit(
    context: RequestContext,
    previous: FieldInspectionProgress,
    next: FieldInspectionProgress,
    change: FieldProgressChange
  ): Promise<void> {
    let action = AuditAction.FieldProgressUpdated;
    if (!previous.outOfOperation && next.outOfOperation) action = AuditAction.FieldLineMarkedOutOfOperation;
    if (previous.outOfOperation && !next.outOfOperation) action = AuditAction.FieldLineReturnedToOperation;
    await this.recordAudit(context, action, next.lineEquipmentId, {
      systemId: next.systemId,
      previousPercent: previous.progressPercent,
      newPercent: next.progressPercent,
      previousStatus: statusOf(previous),
      newStatus: statusOf(next),
      startedAt: next.startedAt,
      completedAt: next.completedAt,
      outOfOperation: next.outOfOperation,
      reason: change.reason ?? change.outOfOperationReason ?? null,
      fieldNote: next.fieldNote
    });
  }

  private async recordAudit(context: RequestContext, action: AuditAction, entityId: string, metadata: Record<string, unknown>): Promise<void> {
    if (!this.audit) return;
    try {
      await this.audit.record(context, {
        action,
        entityType: "FIELD_CONTROL",
        entityId,
        previousState: null,
        newState: null,
        metadata: metadata as never
      });
    } catch (error) {
      console.warn("[CONTROLE DE CAMPO] Falha ao registrar auditoria", error);
    }
  }
}
